import getIndentationMultiplier from '@modules/feedback/components/FeedbackDetails/utils/getIndentationMultiplier';
import { HighlightedFeedbackText } from '@modules/feedback/components/FeedbackHighlighter/HighlightedFeedbackText';
import { Highlight } from '@modules/feedback/types';
import { Avatar, Paper, Stack, Typography } from '@mui/material';
import { ForwardedRef, forwardRef } from 'react';
import { FeedbackDetailsConversationVariant } from 'v2/core/entities/feedback';
import { ForumContent } from 'v2/core/entities/feedback/content';
import ForumMessagePreviewTag from './ForumMessagePreviewTag';

interface ForumMessageTypeLastMessageProps {
  showOriginalLanguage: boolean;
  sourceTitle?: string;
  content?: ForumContent;
  isCollapsed: boolean;
  highlights: {
    [key: string]: Highlight[];
  };
  msgIndex: number;
  variant: FeedbackDetailsConversationVariant;
  isIndentationDisabled: boolean;
}

const ForumMessageTypeLastMessage = (
  {
    showOriginalLanguage,
    sourceTitle,
    content,
    isCollapsed,
    highlights,
    msgIndex,
    variant,
    isIndentationDisabled,
  }: ForumMessageTypeLastMessageProps,
  ref: ForwardedRef<HTMLDivElement | null>,
) => {
  const hasOnlyLastMessage = !content?.isHeader && !content?.parentMsgs;
  const isPreview = variant === 'preview';

  const indentationIdx = getIndentationMultiplier({
    isCollapsed,
    isIndentationDisabled,
    hasOnlyLastMessage,
    parentMsgsLength: content?.parentMsgs?.length || 0,
  });

  return (
    <Paper
      sx={{
        backgroundColor: 'feedback.chatBubble.primary',
        padding: 3,
        borderTopRightRadius: isCollapsed ? 4 : 0,
        borderTopLeftRadius: isCollapsed ? 4 : 0,
        marginLeft: indentationIdx * 4,
        noxShadow: 'none',
      }}
      ref={ref}
    >
      {isPreview && (
        <ForumMessagePreviewTag
          title="Used for predictions"
          sx={{
            margin: '-24px 0 20px -24px',
          }}
        />
      )}
      <Stack spacing={2}>
        {hasOnlyLastMessage && (
          <Typography
            variant="h6"
            fontWeight={500}
            sx={{
              wordBreak: 'break-word',
              opacity: 0.7,
            }}
          >
            {sourceTitle}
          </Typography>
        )}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'divider',
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            {content?.predicted.actorID.slice(0, 1)}
          </Avatar>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              wordBreak: 'break-word',
            }}
          >
            {content?.predicted.actorID}
          </Typography>
        </Stack>
        <Typography
          variant="body1"
          sx={{
            wordBreak: 'break-word',
            borderLeft: '2px solid',
            borderColor: 'divider',
            paddingLeft: 4,
            marginLeft: 1,
            whiteSpace: 'pre-line',
          }}
        >
          <HighlightedFeedbackText
            text={content?.predicted.text!}
            highlights={highlights[msgIndex]}
            showOriginalLanguage={showOriginalLanguage}
            msgIndex={msgIndex}
          />
        </Typography>
      </Stack>
    </Paper>
  );
};

export default forwardRef(ForumMessageTypeLastMessage);
